// Firmark Calcs — Slice 1: the engine provider boundary.
//
// This is the real replacement for the imported design's mock
// `engineProvider.evaluate(req)` (NDS Calc Interface, ~line 815). The design's
// render reads `result.model`; this module produces that model from the REAL
// engine via the api (`/api/calc/compute` for what-if, the sheet PUT for
// persisted recompute) — the UI performs NO structural math (INV-1).
//
// Flow:  prepare(req)  → capability-gate (calcAdapter.buildCalcInputs) + missing-
//                        input validation. Returns either the engine inputs to
//                        send, or a TERMINAL EngineResult (unsupported/incomplete)
//                        with no network call.
//        finish(res)   → wrap a real CalcResult into status:"ok" via toResultModel.
//        evaluateInputs→ stateless what-if (computeCalc) used by the member
//                        selector's per-size candidates; persisted edits go
//                        through the sheet PUT in the workspace and call finish().

import type { CalcInputs, CalcResult } from "../../types/calc";
import type { EngineReq, EngineResult } from "./ndsTypes";
import { buildCalcInputs, toResultModel } from "./calcAdapter";
import { NDS_ROLES } from "./ndsRoles";

function num(v: unknown): number {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

// Missing-input reasons → status:"incomplete" (ported from the design's
// validate(), NDS Calc Interface ~lines 826-830). Phrased as "needs <x>".
export function validateReq(req: EngineReq): string[] {
  const role = NDS_ROLES[req.role];
  if (!role) return [];
  const vals = req.vals ?? {};
  const loads = vals.loads ?? [];
  const reasons: string[] = [];
  if (role.kind === "bending") {
    if (!(num(vals.span) > 0)) reasons.push("a clear span");
    if (!loads.some((l) => !l.axial && !l.lateral && num(l.mag) > 0)) {
      reasons.push("at least one gravity load");
    }
  } else if (role.kind === "axial" || role.kind === "tension") {
    if (!(num(vals.height) > 0)) reasons.push("a member length");
    if (!loads.some((l) => l.axial && num(l.mag) > 0)) {
      reasons.push("at least one axial load");
    }
  } else if (role.kind === "beamcol") {
    if (!(num(vals.height) > 0)) reasons.push("a member height");
    if (role.calculatorId === "wood_biaxial_beam_column_asd") {
      // biaxial: at least one applied ACTION — axial force or M1/M2 moment.
      if (!loads.some((l) => (l.axial || l.momentAxis) && num(l.mag) > 0)) {
        reasons.push("at least one axial load or applied moment");
      }
    } else if (!loads.some((l) => l.axial && num(l.mag) > 0)) {
      // stud (wall): gravity axial required; wind pressure is optional (a
      // pure-gravity sheathed stud is a legitimate check).
      reasons.push("at least one axial load");
    }
  }
  return reasons;
}

export type Prepared =
  | { kind: "compute"; calculatorId: string; inputs: CalcInputs }
  | { kind: "terminal"; result: EngineResult };

// Capability-gate + validate. NO network call. The workspace uses the returned
// `inputs` for the persisted sheet PUT (or rejects with the terminal result).
export function prepare(req: EngineReq): Prepared {
  const built = buildCalcInputs(req);
  if ("unsupported" in built) {
    return { kind: "terminal", result: { status: "unsupported", reasons: built.reasons } };
  }
  const missing = validateReq(req);
  if (missing.length) {
    return { kind: "terminal", result: { status: "incomplete", reasons: missing } };
  }
  return { kind: "compute", calculatorId: built.calculatorId, inputs: built.inputs };
}

// Wrap a real engine CalcResult into the design's status:"ok" envelope.
export function finish(res: CalcResult, req: EngineReq): EngineResult {
  const model = toResultModel(res.calculatorId, res, req);
  return {
    status: "ok",
    model,
    governing: model.gov,
    combos: model.comboResults,
    provenance: model.provenance,
  };
}

// Stateless what-if compute (member-selector candidates, ad-hoc previews).
// Persisted edits go through the sheet PUT in the workspace, then finish().
export async function evaluateInputs(
  req: EngineReq,
  computeFn: (calculatorId: string, inputs: CalcInputs) => Promise<CalcResult>,
): Promise<EngineResult> {
  const p = prepare(req);
  if (p.kind === "terminal") return p.result;
  try {
    const res = await computeFn(p.calculatorId, p.inputs);
    return finish(res, req);
  } catch (e) {
    return { status: "error", error: e instanceof Error ? e.message : "Calculation failed" };
  }
}
